import { environment } from './../../../../environments/environment';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { FormRequest } from './form-request';
import type { Form, FormField } from './interfaces/form.interface';
import { FileUploadAnswer } from '../../utils';
import { HttpClient } from '@angular/common/http';
import { DropdownData, InputType } from './interfaces/input.interface';
import { RequestHandler } from '../../../../../src/app/service/OffService/request-handler';
import { FormOperations } from './utils/form-operations';
import { FormCustomValidators } from './utils/form-custom-validators';

@Component({
  selector: 'app-reactive-form-builder',
  standalone: false,
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactiveFormBuilderComponent<T>
  extends FormRequest
  implements OnInit
{
  @Input() form!: Form<T>;
  @Input() serverUrl = '';
  @Input() request!: RequestHandler<any>;
  @Input() getRequest!: RequestHandler<any>
  @Input() undoEnabled = false;
  @Input() previousData: T | null = null;
  @Input() recordHasAssociatedData = false;
  @Input() dropdownLoadTrigger!: Subject<void>;
  @Output() formChanges: EventEmitter<[[object, string], string]> =
    new EventEmitter<[[object, string], string]>();
  @Output() goBack: EventEmitter<T> = new EventEmitter<T>();
  @Output() afterSend: EventEmitter<T> = new EventEmitter<T>();
  @Output() hideDialog: EventEmitter<T> = new EventEmitter<T>();

  public appName = environment.appName;
  public filterInputParcelas = environment.features.filterInputParcelas;
  public INPUT = InputType;
  public model!: FormGroup;
  public originalModel: Record<string, string> = {};

  private previousModel: Record<string, string> = {};
  private formOperations!: FormOperations<T>;
  private formCustomValidators = new FormCustomValidators();

  /**
   *
   * @param http
   */
  constructor(private http: HttpClient) {
    super();
  } // ();

  /**
   * Populates the view with form fields.
   */
  ngOnInit(): void {
    if (this.request){
      this.request.hasError = undefined;
    }
    this.initializeForm();
  } // ();

  /**
   * Returns the user to the location from which they arrived at the form.
   */
  public returnToMainView(): void {
    this.goBack.emit();
  } // ();

  public executePostSubmissionLogic(res: any): void {
    this.afterSend.emit(res);
  }
  
  /**
   * Returns the user to the location from which they arrived at the form.
   */
  public closeDialog(): void {
    this.hideDialog.emit();
  } // ();

  /**
   * Uploads signature image to the server.
   * @param event Signature file.
   */
  public async getSignature(event: File) {
    this.model.patchValue({ ['signature_img']: event });

    const signatureName = this.model.get('signature_img')?.value;

    if (signatureName == null) {
      return;
    }

    const formData = new FormData();

    formData.append('fileToUpload', signatureName);
    formData.append('database', 'tareas_fitosanitarias');
    formData.append('folder', 'fitosanitarios');
    formData.append('field', 'signature_img');

    const signature = await firstValueFrom(
      this.http.post<FileUploadAnswer>(
        this.serverUrl + 'ws/tareas/fileUploadGeneric.php',
        formData
      )
    );

    this.model.patchValue({
      ['signature_img']: signature.target_file.replace(
        '../../images/fitosanitarios/',
        ''
      ),
    });
  } // ();

  /**
   * Displays the image of the signature uploaded to the form.
   * @param event Signature file name.
   */
  public updateFirma(event: string) {
    this.model.patchValue({ ['signature_img']: event });
  } // ();

  public updateImageModel(event: string, fieldName: string) {
    this.model.patchValue({ [fieldName]: event });
  }

  public executeFunction(onClick: any) {
    onClick();
  }

  public updateForm(data: any, emit: boolean) {
    this.model.patchValue(data, { emitEvent: emit });
  }

  /**
   * Initializes the form in two runs: the input run, where all the form fields are loaded,
   * and the dropdown run, where the dropdowns are populated with data
   * so that the form is always displayed to the user.
   * @returns void
   */
  private initializeForm(): void {
    this.formOperations = new FormOperations(this.form, this.model);
    this.initializeModel();

    if ((this.form.type <= 2 || this.form.type === 6) && this.dropdownLoadTrigger) {
      const subscription = this.dropdownLoadTrigger.subscribe(() => {
        this.initializeModel();
        this.subscribeToFormChanges();
        this.originalModel = { ...this.model.value };
        this.formOperations.executeFieldsLogic(null);

        subscription.unsubscribe();
      });
    } // if();
  } // ();

  /**
   * Converts form fields to form group.
   */
  private initializeModel(): void {
    this.model = this.toFormGroup(this.form);
    this.formOperations = new FormOperations(this.form, this.model);
  } // ();

  /**
   * Updates form fields based on a change made by the user.
   */
  private subscribeToFormChanges(): void {
    this.model.valueChanges.subscribe((newValues) => {
      const changedFields = Object.keys(newValues).filter(
        (control) => newValues[control] !== this.previousModel?.[control]
      );

      if (changedFields.length > 0) {
        changedFields.forEach((changedField) => {
          this.formChanges.emit([newValues, changedField]);
          this.formOperations.applySelected(
            this.formOperations.getFieldByControlName(changedField),
            newValues[changedField]
          );
          this.formOperations.executeFieldsLogic(changedField);
        });
      } // if();

      this.previousModel = { ...newValues };
    });
  } // ();

  /**
   * Converts form to FormGroup.
   * @param form
   * @returns FormGroup
   */
  private toFormGroup(form: Form<T>): FormGroup {
    const controls = form.sections
      .filter((section) => section.isVisible)
      .flatMap((section) => section.fields)
      .filter(
        (field) => field.conditions.isVisible && !field.conditions.isRetained
      )
      .reduce((group, field) => {
        const defaultValue = this.setDefaultValues(field);

        // eslint-disable-next-line no-param-reassign
        group[field.name ?? ''] = new FormControl(
          defaultValue,
          this.setValidators(field)
        );
        this.formOperations.applySelected(
          this.formOperations.getFieldByControlName(field.name ?? ''),
          defaultValue as T
        );
        return group;
      }, {} as Record<string, FormControl>);

    return new FormGroup(controls);
  } // ();update

  /**
   * Returns a safe default value for a field.
   * - Multiselect fields ALWAYS return an array.
   * - Single value fields return '' (empty string) unless a default/previous value exists.
   */
  private setDefaultValues(field: FormField<T>): unknown {
    const isMulti =
      field?.input?.type === InputType.MULTISELECT ||
      field?.input?.type === InputType.MULTISELECT_SEARCH;

    // 0) If we must NOT remember on duplicate, still return a safe default
    if (this.form.type === 2 && field.conditions.isRememberedOnDuplicate === false) {
      return isMulti ? [] : '';
    }

    // 1) Previous value (locate with ids_* alias for *_nombres fields)
    const key = field.name ?? '';
    const aliasKey = key.endsWith('_nombres') ? `ids_${key.replace(/_nombres$/, '')}` : undefined;
    const prevRaw =
      (this.previousData as any)?.[key] ??
      (aliasKey ? (this.previousData as any)?.[aliasKey] : undefined);

    // Helper: parse string to array by ; or ,
    const splitIds = (s: string) =>
      s.split(/[;,]/).map(v => v.trim()).filter(Boolean);

    // Helper: get option list [{label,value}, ...]
    const getOptions = () => {
      const dd = field?.input?.data as DropdownData<any>;
      return (dd?.values ?? dd?.filtered ?? []) as Array<{ label?: string; value: any }>;
    };

    // Helper: map id -> value (comparing by valuePrimaryKey)
    const mapIdToValue = (id: string | number) => {
      const pk = field.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
      const opts = getOptions();
      const found = opts.find(o => {
        const v = (o && typeof o === 'object') ? (o as any).value : o;
        return v && String(v?.[pk]) === String(id);
      });
      return found ? (found as any).value : null;
    };

    // Previous value exists
    if (prevRaw !== undefined && prevRaw !== null && String(prevRaw) !== 'null') {
      if (isMulti) {
        // Normalize to array of ids/objects
        let idsOrObjs: any[] = [];
        if (Array.isArray(prevRaw)) {
          idsOrObjs = prevRaw;
        } else if (typeof prevRaw === 'string') {
          try {
            const parsed = JSON.parse(prevRaw);
            if (Array.isArray(parsed)) {
              idsOrObjs = parsed;
            } else {
              idsOrObjs = splitIds(prevRaw);
            }
          } catch {
            idsOrObjs = splitIds(prevRaw);
          }
        } else {
          idsOrObjs = [];
        }

        // If already objects with pk, return as is
        const pk = field.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const looksLikeObjects = idsOrObjs.every(x => x && typeof x === 'object' && x[pk] != null);

        if (looksLikeObjects) {
          return idsOrObjs;
        }

        // If they're ids, convert to actual "value" objects
        const mapped = idsOrObjs
          .map(id => mapIdToValue(id))
          .filter(v => v != null);

        // If data not loaded yet, return ids to avoid breaking;
        // when dropdownLoadTrigger fires, it will re-initialize and map correctly
        return mapped.length > 0 ? mapped : idsOrObjs;
      }

      // Single-value field
      if (field.input?.dropdownConfiguration?.valuePrimaryKey) {
        const mapped = mapIdToValue(String(prevRaw));
        return mapped ?? prevRaw;
      }

      return prevRaw;
    }

    // 2) No previous value → fallbacks
    if (field.input?.defaultValue == null) {
      const opts = getOptions();
      if (Array.isArray(opts) && opts.length === 1) {
        return (opts[0] as any).value ?? (isMulti ? [] : '');
      }
      return isMulti ? [] : '';
    }

    // 3) Explicit default value present
    if (isMulti) {
      const def = field.input.defaultValue as unknown;
      if (Array.isArray(def)) return def;
      if (typeof def === 'string' && def.length > 0) {
        try {
          const parsed = JSON.parse(def);
          if (Array.isArray(parsed)) return parsed;
        } catch { /* noop */ }
        const ids = splitIds(def);
        const mapped = ids.map(id => mapIdToValue(id)).filter(v => v != null);
        return mapped.length > 0 ? mapped : ids;
      }
      return [];
    }

    // Single-value field with default
    if (field.input?.dropdownConfiguration?.valuePrimaryKey && typeof field.input.defaultValue === 'string') {
      const mapped = mapIdToValue(field.input.defaultValue);
      return mapped ?? field.input.defaultValue;
    }

    return field.input.defaultValue;
  }

  /**
   *
   * @param field
   * @returns Validators
   */
  private setValidators(field: FormField<T>): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (field.conditions.isRequired) {
      validators.push(Validators.required);
    }
    if (field.input.maxLength != null) {
      validators.push(Validators.maxLength(field.input.maxLength));
    }
    if (field.name?.includes('hora')) {
      validators.push(this.formCustomValidators.timeValidator);
    }

    if (field.name?.includes('email')) {
      validators.push(Validators.email);
    }

    if (field.name?.includes('nif') || field.name?.includes('cif')) {
      validators.push(this.formCustomValidators.spanishIdValidator);

      if (typeof field.checkForDuplicates === 'function') {
        validators.push(
          this.formCustomValidators.createDuplicateSpanishIdValidator(field.checkForDuplicates));
      }
    }

    return validators;
  } // ();
} // class;
